import { ChangeEvent } from 'react';
import compact from 'lodash/compact';
import { SxProps } from 'src/theme/types';
import Select from 'src/components/Select/Select';
import Switch from 'src/components/Switch';
import TextField from '@mui/material/TextField';
import * as Yup from 'yup';
import TextFieldWithInfo from 'src/components/TextField';
import { nanToUndefined } from 'src/utils';
import AutoCompleteWithFreeText from 'src/components/AutoComplete/AutoCompleteWithFreeText';
import {
  SolutionAlertAggType,
  SolutionAlertFilterField,
  SolutionAlertItemField,
} from '../types';
import {
  ALERT_METRIC_AGG_TYPE,
  ALERT_METRIC_AGG_TYPE_ONE,
  ALERT_METRIC_ISVALUE,
  ALERT_METRIC_OF,
  ALERT_RULE_TYPE,
  ALERT_SEVERITY,
  ALERT_TIME_UNITS,
  NOTIFICATION_PREFERENCE_OPTIONS,
} from './constant';
import SolutionAlertRuleEditFormRow from './components/SolutionAlertEditFormRow';

const inputStyle: SxProps = { width: ({ spacing }) => spacing(40) };

export const getAlertRuleFormFields = (
  isEditMode: boolean,
  aggType?: string,
  handleKeyDown?: (event: ChangeEvent<HTMLInputElement>) => void,
  data?: any,
  defaultCIName?: string,

  options?: any,
  optionsLoading?: boolean,
  dataOptions?: any,
  dataLoading?: boolean
) =>
  compact([
    {
      name: SolutionAlertItemField.Name,
      label: 'Name',
      Component: TextField,
      placeholder: 'Enter name',
      required: true,
      validation: Yup.string().required('Name is required'),
      sx: inputStyle,
    },
    {
      name: SolutionAlertItemField.Severity,
      label: 'Severity',
      Component: Select,
      placeholder: 'Select the option',
      required: true,
      validation: Yup.string().required('Severity is required'),
      options: ALERT_SEVERITY,
      sx: inputStyle,
    },
    {
      name: SolutionAlertItemField.EnableRule,
      label: 'Enable Rule',
      Component: Switch,
      type: 'checkbox',
      required: false,
      labelStyle: { color: '#7B7B7B', opacity: '80%' },
      sx: inputStyle,
      // disabled: isEditMode,
    },
    {
      name: SolutionAlertItemField.AlertRuleType,
      label: 'Alert Rule Type',
      Component: Select,
      placeholder: 'Select the option',
      required: true,
      options: ALERT_RULE_TYPE,
      validation: Yup.string().required('Alert Rule Type is required'),
      sx: inputStyle,
    },
    {
      type: 'heading',
      name: 'Metric Threshold',
      label: 'Metric Threshold',
      showInfoIcon: true,
      infoTitle: 'Alert when the metrics aggregation exceeds the threshold.',
    },
    {
      name: SolutionAlertItemField.WHEN,
      label: 'WHEN',
      Component: Select,
      placeholder: 'Select the option',
      required: true,
      validation: Yup.string().required('When Field is required'),
      options: ALERT_METRIC_AGG_TYPE_ONE,
      sx: inputStyle,
    },
    aggType !== SolutionAlertAggType.CustomEquation && {
      name: SolutionAlertItemField.OF,
      label: 'OF',
      Component: Select,
      placeholder: 'Select the option',
      required: true,
      validation: Yup.string().required('OF Field is required'),
      options: ALERT_METRIC_OF,
      sx: inputStyle,
    },
    {
      name: SolutionAlertItemField.IsValue,
      label: 'Comparator',
      Component: Select,
      placeholder: 'Select the option',
      required: true,
      validation: Yup.string().required('Comparator Field is required'),
      options: ALERT_METRIC_ISVALUE,
      sx: inputStyle,
    },
    {
      name: SolutionAlertItemField.ThresholdValues,
      label: 'Threshold Value',
      Component: TextField,
      placeholder: 'Enter the value',
      type: 'number',
      required: true,
      initialValue: 0,
      onKeyDown: handleKeyDown,
      inputProps: {
        min: 0,
      },
      validation: Yup.number()
        .typeError('Please enter a valid number')
        .test('is-integer', 'Please enter a whole number', (value) =>
          Number.isInteger(value)
        )
        .transform(nanToUndefined)
        .required('Threshold Value is required'),
      sx: {
        width: ({ spacing }) => spacing(40),
      },
    },
    aggType === SolutionAlertAggType.CustomEquation && {
      type: 'heading',
      name: '',
      label: '',
      showFieldWidth: true,
      width: ({ spacing }) => spacing(40),
    },
    aggType === SolutionAlertAggType.CustomEquation && {
      name: SolutionAlertItemField.AggregationName,
      label: `Aggregation Name`,
      Component: TextField,
      required: true,
      value: 'A',
      inputProps: {
        readOnly: true,
      },
      sx: { width: ({ spacing }) => spacing(18) },
    },
    aggType === SolutionAlertAggType.CustomEquation && {
      name: SolutionAlertItemField.AggregationValue,
      label: 'Operation',
      Component: Select,
      placeholder: 'Select the option',
      required: true,
      validation: Yup.string().required('Operation Field is required'),
      options: ALERT_METRIC_AGG_TYPE,
      sx: { width: ({ spacing }) => spacing(23) },
    },
    aggType === SolutionAlertAggType.CustomEquation && {
      name: SolutionAlertItemField.FieldValue,
      label: 'Field',
      Component: Select,
      placeholder: 'Select the option',
      required: true,
      validation: Yup.string().required('Field is required'),
      options: ALERT_METRIC_OF,
      sx: { width: ({ spacing }) => spacing(38) },
    },
    aggType === SolutionAlertAggType.CustomEquation && {
      name: SolutionAlertItemField.CustomMetrics,
      appendLabel: 'Add Aggregation',
      childFields: getSolutionAlertsFilterFields(),
      RowComponent: SolutionAlertRuleEditFormRow,
      maxRows: 4,
      showDefaultRow: true,
    },
    aggType === SolutionAlertAggType.CustomEquation && {
      name: SolutionAlertItemField.Equation,
      label: 'Equation',
      Component: TextFieldWithInfo,
      placeholder: 'Enter the expression',
      required: true,
      validation: Yup.string()
        .matches(/^[^a-z]+$/, 'Please enter a letter between A and Z')
        .required('Equation is required'),
      showinfoicon: true,
      infotitle: 'Supports basic math expressions, Eg: A+B',
      sx: {
        width: ({ spacing }) => spacing(40),
        marginRight: '10px',
      },
    },
    aggType === SolutionAlertAggType.CustomEquation && {
      type: 'heading',
      name: '',
      label: '',
      showFieldWidth: true,
      width: ({ spacing }) => spacing(30),
    },
    {
      type: 'heading',
      name: 'FOR THE LAST',
      label: 'FOR THE LAST',
      showFieldWidth: true,
      width: ({ spacing }) => spacing(40),
    },
    {
      type: 'heading',
      name: 'Check Every',
      label: 'Check Every',
      showFieldWidth: true,
      width: ({ spacing }) => spacing(40),
    },
    {
      name: SolutionAlertItemField.LastValue,
      label: 'Threshold Value',
      Component: TextField,
      type: 'number',
      initialValue: 0,
      required: true,
      validation: Yup.number()
        .typeError('Please enter a valid number')
        .test('is-integer', 'Please enter a whole number', (value) =>
          Number.isInteger(value)
        )
        .transform(nanToUndefined)
        .required('Threshold Value is required'),
      onKeyDown: handleKeyDown,
      inputProps: { min: 0 },
      placeholder: 'Enter the value',
      sx: { width: ({ spacing }) => spacing(19) },
    },
    {
      name: SolutionAlertItemField.LastTimeUnits,
      label: 'Time Units',
      Component: Select,
      required: true,
      validation: Yup.string().required('Time Units is required'),
      placeholder: 'Select the option',
      options: ALERT_TIME_UNITS,
      sx: { width: ({ spacing }) => spacing(19) },
    },
    {
      name: SolutionAlertItemField.EveryValue,
      label: 'Threshold Value',
      Component: TextField,
      type: 'number',
      initialValue: 0,
      required: true,
      validation: Yup.number()
        .typeError('Please enter a valid number')
        .test('is-integer', 'Please enter a whole number', (value) =>
          Number.isInteger(value)
        )
        .transform(nanToUndefined)
        .required('Threshold Value is required'),
      inputProps: { min: 0 },
      onKeyDown: handleKeyDown,
      placeholder: 'Enter the value',
      sx: { width: ({ spacing }) => spacing(19) },
    },
    {
      name: SolutionAlertItemField.EveryTimeUnits,
      label: 'Time Units',
      Component: Select,
      required: true,
      validation: Yup.string().required('Time Units is required'),
      placeholder: 'Select the option',
      options: ALERT_TIME_UNITS,
      sx: { width: ({ spacing }) => spacing(19) },
    },
    {
      type: 'heading',
      name: 'Notification Preference',
      label: 'Notification Preference',
    },
    {
      name: SolutionAlertItemField.CiName,
      label: 'ServiceNow CI Name',
      Component: TextField,
      placeholder: 'Enter name',
      required: true,
      initialValue: defaultCIName,
      validation: Yup.string().required('Name is required'),
      sx: inputStyle,
    },
    {
      name: SolutionAlertItemField.subscriptionId,
      label: 'Subscription',
      Component: Select,
      placeholder: 'Select the option',
      options: data,
      sx: inputStyle,
    },
    {
      name: SolutionAlertItemField.notifications,
      label: 'Notification',
      Component: AutoCompleteWithFreeText,
      options: NOTIFICATION_PREFERENCE_OPTIONS,
      multiple: true,
      sx: { width: ({ spacing }) => spacing(40) },
    },
    {
      name: SolutionAlertItemField.groupIds,
      label: 'Groups',
      options: dataOptions,
      Component: AutoCompleteWithFreeText,
      multiple: true,
      loading: dataLoading,
      sx: { width: ({ spacing }) => spacing(40) },
    },
    {
      name: SolutionAlertItemField.siteIds,
      label: 'Sites',
      options,
      Component: AutoCompleteWithFreeText,
      multiple: true,
      loading: optionsLoading,
      sx: { width: ({ spacing }) => spacing(40) },
    },
  ]);

export const getSolutionAlertsFilterFields = () => [
  {
    name: SolutionAlertFilterField.Name,
    label: `Aggregation Name`,
    showLabel: true,
    Component: TextField,
    inputProps: {
      readOnly: true,
    },
    sx: { width: ({ spacing }) => spacing(18) },
  },
  {
    name: SolutionAlertFilterField.AggType,
    label: `Operation `,
    Component: Select,
    placeholder: 'Select the option',
    options: ALERT_METRIC_AGG_TYPE,
    sx: { width: ({ spacing }) => spacing(23) },
  },
  {
    name: SolutionAlertFilterField.Field,
    label: `Field `,
    placeholder: 'Select the value',
    Component: Select,
    options: ALERT_METRIC_OF,
    sx: { width: ({ spacing }) => spacing(38) },
  },
];
